import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Group, Modal, Select, Stack, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { normalizeErrorString } from '@medplum/core'
import { IconAlertCircle, IconCirclePlus } from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import {
  INSURANCE_LOB_CATEGORY_OPTIONS,
  INSURANCE_PRODUCT_TYPE_OPTIONS,
} from '@/pages/Settings/insurancePlans.constants'
import {
  getInsurancePayerDetails,
  getInsurancePayersList,
  upsertInsurancePlan,
  type InsurancePayerOption,
  type InsurancePlanListItem,
} from '@/pages/Settings/insurancePlans.service'
import type { InsurancePlanSnapshot } from '@/types/insuranceTypes'
import { states } from '@/utils/states'
import { FIELD_CLASSES } from '@/components/ui/formModal.tokens'

type InsurancePlansDropdownProps = {
  payerId?: string
  state?: string | null
  value: InsurancePlanSnapshot | null
  onChange: (value: InsurancePlanSnapshot | null) => void
  onStateChange?: (value: string | null) => void
  disabled?: boolean
}

const CREATE_NEW_PLAN_VALUE = '__create_new_plan__'

const EMPTY_CREATE_PLAN_VALUES: InsurancePlanSnapshot = {
  planName: '',
  productType: '',
  planId: '',
  lobCategory: '',
}

export function InsurancePlansDropdown({
  payerId,
  state,
  value,
  onChange,
  onStateChange,
  disabled,
}: InsurancePlansDropdownProps) {
  const middlewareClient = useMiddlewareClient()
  const [payerOptions, setPayerOptions] = useState<InsurancePayerOption[] | null>(null)
  const [payerDetails, setPayerDetails] = useState<{
    id: string
    plans: InsurancePlanListItem[]
  } | null>(null)
  const [localState, setLocalState] = useState<string | null>(state?.trim() ? state : null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createPlanValues, setCreatePlanValues] =
    useState<InsurancePlanSnapshot>(EMPTY_CREATE_PLAN_VALUES)

  const authoritativeState = state?.trim() ? state.trim().toUpperCase() : null
  const effectiveState = authoritativeState ?? localState
  const usesInternalStateSelector = !authoritativeState

  useEffect(() => {
    if (authoritativeState) {
      setLocalState(authoritativeState)
    }
  }, [authoritativeState])

  useEffect(() => {
    if (!payerId || payerOptions) {
      return
    }

    let active = true
    void (async () => {
      try {
        const nextPayers = await getInsurancePayersList(middlewareClient)
        if (active) {
          setPayerOptions(nextPayers)
        }
      } catch (error) {
        if (active) {
          setLoadError(normalizeErrorString(error) || 'Payer not found')
        }
      }
    })()

    return () => {
      active = false
    }
  }, [middlewareClient, payerId, payerOptions])

  const matchedPayer = useMemo(
    () =>
      payerOptions?.find(
        (option) => option.payerId === payerId && option.state?.toUpperCase() === effectiveState,
      ) ?? null,
    [effectiveState, payerId, payerOptions],
  )

  const loadPayerDetails = async (payerBlockId: string) => {
    setIsLoading(true)
    try {
      const result = await getInsurancePayerDetails(middlewareClient, payerBlockId)
      setPayerDetails({ id: result.id, plans: result.plans })
      setLoadError(null)
      return result
    } catch (error) {
      setPayerDetails(null)
      setLoadError(normalizeErrorString(error) || 'Payer not found')
      return null
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!payerId || !effectiveState) {
      setPayerDetails(null)
      setLoadError(null)
      return
    }

    if (!payerOptions) {
      return
    }

    if (!matchedPayer) {
      setPayerDetails(null)
      setLoadError('Payer not found')
      return
    }

    loadPayerDetails(matchedPayer.id).then((result) => {
      if (result) {
        onStateChange?.(effectiveState)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveState, matchedPayer, payerId, payerOptions])

  const plansWithOrphan = useMemo(() => {
    const plans = payerDetails?.plans ?? []
    if (!value?.planId || plans.some((plan) => plan.id === value.planId)) {
      return plans
    }

    return [
      ...plans,
      {
        id: value.planId,
        name: value.planName,
        productType: value.productType,
        lobCategory: value.lobCategory,
        source: 'custom' as const,
        editable: false,
        deletable: false,
      },
    ]
  }, [payerDetails?.plans, value])

  const hasOrphanValue =
    Boolean(value?.planId) &&
    Boolean(payerDetails) &&
    payerDetails &&
    !payerDetails.plans.some((plan) => plan.id === value?.planId)

  const selectData = useMemo(() => {
    const planOptions = plansWithOrphan.map((plan) => ({
      value: plan.id,
      label: `${plan.name} (${plan.id})`,
    }))

    if (payerDetails && !loadError) {
      planOptions.push({ value: CREATE_NEW_PLAN_VALUE, label: '+ Create new' })
    }

    return planOptions
  }, [loadError, payerDetails, plansWithOrphan])

  const handleCreatePlanSubmit = async () => {
    if (!payerDetails?.id) {
      return
    }

    if (
      !createPlanValues.planName.trim() ||
      !createPlanValues.productType.trim() ||
      !createPlanValues.planId.trim() ||
      !createPlanValues.lobCategory.trim()
    ) {
      notifications.show({
        title: 'Missing fields',
        message: 'Fill out all plan fields before creating a custom insurance plan.',
        color: 'red',
      })
      return
    }

    setIsSaving(true)
    try {
      await upsertInsurancePlan(middlewareClient, {
        payerBlockId: payerDetails.id,
        planName: createPlanValues.planName.trim(),
        productType: createPlanValues.productType.trim(),
        planId: createPlanValues.planId.trim(),
        lobCategory: createPlanValues.lobCategory.trim(),
      })

      const refreshed = await loadPayerDetails(payerDetails.id)
      const nextPlan = refreshed?.plans.find((plan) => plan.id === createPlanValues.planId.trim())

      if (nextPlan) {
        onChange({
          planName: nextPlan.name,
          productType: nextPlan.productType,
          planId: nextPlan.id,
          lobCategory: nextPlan.lobCategory,
        })
      }

      setCreatePlanValues(EMPTY_CREATE_PLAN_VALUES)
      setIsCreateModalOpen(false)
    } catch (error) {
      notifications.show({
        title: 'Plan not created',
        message: normalizeErrorString(error) || 'Failed to create a custom insurance plan.',
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {usesInternalStateSelector && (
          <Select
            label="State"
            placeholder="Select a state"
            data={states.map((item) => ({ value: item.value, label: item.label }))}
            value={localState}
            onChange={(nextValue) => {
              const normalized = nextValue?.toUpperCase() ?? null
              setLocalState(normalized)
            }}
            searchable
            disabled={disabled || !payerId}
            classNames={FIELD_CLASSES}
          />
        )}

        {hasOrphanValue && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
            className="sm:col-span-2"
          >
            The saved insurance plan is no longer in the payer catalog. It will be kept as a
            snapshot unless you choose a different plan.
          </Alert>
        )}

        <Select
          className={usesInternalStateSelector ? undefined : 'sm:col-span-2'}
          classNames={FIELD_CLASSES}
          label="Insurance plan"
          placeholder={
            !payerId
              ? 'Select an insurance provider first'
              : !effectiveState
                ? 'Select a state first'
                : isLoading
                  ? 'Loading plans...'
                  : 'Select a plan'
          }
          data={selectData}
          value={value?.planId || null}
          onChange={(nextValue) => {
            if (nextValue === CREATE_NEW_PLAN_VALUE) {
              setCreatePlanValues(value ?? EMPTY_CREATE_PLAN_VALUES)
              setIsCreateModalOpen(true)
              return
            }

            const selectedPlan = plansWithOrphan.find((plan) => plan.id === nextValue)
            if (!selectedPlan) {
              onChange(null)
              return
            }

            onChange({
              planName: selectedPlan.name,
              productType: selectedPlan.productType,
              planId: selectedPlan.id,
              lobCategory: selectedPlan.lobCategory,
            })
          }}
          error={loadError || undefined}
          disabled={disabled || !payerId || !effectiveState || isLoading}
          searchable
        />
      </div>

      <Modal
        opened={isCreateModalOpen}
        onClose={() => {
          if (!isSaving) {
            setIsCreateModalOpen(false)
          }
        }}
        title="Create custom insurance plan"
      >
        <Stack>
          <TextInput
            label="Plan Name"
            value={createPlanValues.planName}
            onChange={(event) =>
              setCreatePlanValues((current) => ({
                ...current,
                planName: event.currentTarget.value,
              }))
            }
          />
          <Select
            label="Product Type"
            data={INSURANCE_PRODUCT_TYPE_OPTIONS}
            value={createPlanValues.productType}
            onChange={(nextValue) =>
              setCreatePlanValues((current) => ({
                ...current,
                productType: nextValue ?? '',
              }))
            }
            searchable
          />
          <TextInput
            label="Plan ID"
            value={createPlanValues.planId}
            onChange={(event) =>
              setCreatePlanValues((current) => ({
                ...current,
                planId: event.currentTarget.value,
              }))
            }
          />
          <Select
            label="LOB Category"
            data={INSURANCE_LOB_CATEGORY_OPTIONS}
            value={createPlanValues.lobCategory}
            onChange={(nextValue) =>
              setCreatePlanValues((current) => ({
                ...current,
                lobCategory: nextValue ?? '',
              }))
            }
            searchable
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              leftSection={<IconCirclePlus size={16} />}
              onClick={() => void handleCreatePlanSubmit()}
              loading={isSaving}
            >
              Create plan
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
